import ExportButton from '../../components/ExportButton';
import CalendarDateField from '../../components/CalendarDateField';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Boxes, Package, Scale, Weight, Pencil, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePermissions } from '../../auth/permissions';
import { getCustomerDetails, updateCustomer } from './customerService';
import CustomerModal from '../../components/CustomerModal';
import Toast from '../../components/Toast';
import FilterMenu from '../../components/FilterMenu';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import CustomerInventoryTable, { formatCargoNumber } from './CustomerInventoryTable';
import { exportCargoManifest, filterInventory } from './customerInventory';
import '../warehouses/warehouses.css';
import '../inventory/inventory.css';
import '../../components/export-button.css';
import './customer-details.css';

const PAGE_SIZE = 15;
function currentMonthRange() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  return { from: `${year}-${month}-01`, to: `${year}-${month}-${new Date(year, today.getMonth() + 1, 0).getDate()}` };
}

export default function CustomerDetailsPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [appliedDates, setAppliedDates] = useState(currentMonthRange);
  const [dateFrom, setDateFrom] = useState(() => currentMonthRange().from);
  const [dateTo, setDateTo] = useState(() => currentMonthRange().to);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    let active = true;
    setDetails(null); setError(null); setEditing(false); setQuery(''); setStatus(''); const range = currentMonthRange(); setDateFrom(range.from); setDateTo(range.to); setAppliedDates(range); setPage(1);
    getCustomerDetails(customerId).then(data => { if (active) setDetails(data); }).catch(err => { if (active) setError(err); });
    return () => { active = false; };
  }, [customerId, attempt]);
  const containerStatuses = { '': 'All Status', ...Object.fromEntries((details?.statuses || []).map(row => [row.key, row.label])) };
  const statusInventory = useMemo(() => (details?.inventory || []).map(item => ({ ...item,
    packaging: item.packaging.map(pack => ({ ...pack, batches: pack.batches.filter(batch => !status ||
      (batch.container_statuses || item.container_statuses || ['Planning']).includes(status)) })).filter(pack => pack.batches.length).map(pack => !status ? pack : ({ ...pack,
      ...Object.fromEntries(['gross_weight', 'net_weight', 'cbm'].map(field => [field, pack.batches.reduce((sum, batch) => sum + Number(batch[field] ?? batch.products.reduce((n, product) => n + Number(product[field] || 0), 0)), 0)])),
      total_carton_qty: pack.batches.reduce((sum, batch) => sum + Number(batch.batch_quantity || 0), 0),
    })),
  })).filter(item => item.packaging.length), [details, status]);
  const dateInventory = useMemo(() => statusInventory.filter(item => {
    if (!appliedDates.from && !appliedDates.to) return true;
    const date = typeof item.inventory_date === 'string' ? item.inventory_date.slice(0, 10) : '';
    return date && (!appliedDates.from || date >= appliedDates.from) && (!appliedDates.to || date <= appliedDates.to);
  }), [statusInventory, appliedDates]);
  const filtered = useMemo(() => filterInventory(dateInventory, query), [dateInventory, query]);
  const statusSummary = useMemo(() => {
    if (!status && !query.trim() && details && filtered.length === details.inventory.length && filtered.length) return details.summary;
    const products = filtered.flatMap(item => item.packaging.flatMap(pack => pack.batches.flatMap(batch => batch.products)));
    const batches = filtered.flatMap(item => item.packaging.flatMap(pack => pack.batches));
    const batchWeight = field => batches.reduce((total, batch) => total + Number(batch[field] ?? batch.products.reduce((sum, product) => sum + Number(product[field] || 0), 0)), 0);
    return {
      totalProducts: new Set(products.map(product => product.product_id || product.name)).size,
      totalQuantity: products.reduce((total, product) => total + Number(product.quantity || 0), 0),
      totalNetWeight: batchWeight('net_weight'),
      totalGrossWeight: batchWeight('gross_weight'),
    };
  }, [details, filtered, query]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  async function saveCustomer(payload) {
    let customer;
      try {
        customer = await updateCustomer(customerId, payload);
      } catch (error) {
        if (error.status === 403 || error.status === 404) {
          setEditing(false);
          setToast({ tone: 'error', message: "You don't have permission to edit this customer." });
          return;
        }
        throw error;
      }
    setDetails(current => ({ ...current, customer })); setEditing(false);
    setToast({ tone: 'success', message: 'Customer updated successfully.' });
  }
  async function exportManifest(format) {
    setExporting(true);
    try { await exportCargoManifest(details, format); }
    catch (err) { setToast({ tone: 'error', message: err.message || 'Unable to export cargo manifest.' }); }
    finally { setExporting(false); }
  }
  if (error) return <section className="wh-page wh-list-page"><div className="wh-panel"><p role="alert">{error.message}</p>{error.status !== 404 && <button className="inv-export" onClick={() => setAttempt(value => value + 1)}>Retry</button>}</div></section>;
  if (!details) return <section className="wh-page wh-list-page" aria-busy="true"><p>Loading customer inventory...</p></section>;
  const cards = [
    [Boxes, 'blue', 'Total Products', statusSummary.totalProducts, 'SKUs'],
    [Package, 'amber', 'Total Quantity', statusSummary.totalQuantity, ''],
    [Scale, 'cyan', 'Total Net Weight', statusSummary.totalNetWeight, 'kg'],
    [Weight, 'pink', 'Total Gross Weight', statusSummary.totalGrossWeight, 'kg'],
  ];
  return <section className="wh-page wh-list-page inv-page customer-details-page">
    <header className="wh-title"><div><CompactBreadcrumb parent="Customers" current={`${details.customer.name} Details`} onNavigate={() => navigate('/customers')} /><h1>{details.customer.name}</h1></div><div className="wh-title-actions">
      <ExportButton disabled={exporting} onExport={exportManifest}>Export Cargo Manifest</ExportButton>
      {canWrite('Customers') && <button className="wh-add" onClick={() => setEditing(true)}><Pencil size={14} />Edit Customer</button>}
    </div></header>
    <div className="wh-kpis customer-cargo-kpis">{cards.map(([Icon, tone, label, value, unit]) => <article className="wh-kpi" key={label}>
      <span className={`wh-kpi-icon ${tone}`}><Icon size={18} /></span><div className="wh-kpi-copy"><span>{label}</span><strong>{formatCargoNumber(value)} <small>{unit}</small></strong></div>
    </article>)}</div>
    <div className="wh-panel"><div className="customer-cargo-heading"><div className="customer-cargo-title"><h2>Associated Goods &amp; Stored Inventory</h2></div>
      <div className="customer-cargo-filters"><label className="wh-search"><Search size={14} /><input type="search" placeholder="Filter by product, SKU, or lot..." value={query} onChange={event => { setQuery(event.target.value); setPage(1); }} /></label>
        <FilterMenu className="customer-status-filter" icon={<span className="customer-status-dot" />} label="Container Status" value={containerStatuses[status]} ariaLabel="Filter inventory status" open={statusOpen} onToggle={() => setStatusOpen(current => !current)} onClose={() => setStatusOpen(false)}>
        <div className="wh-menu-list customer-status-options">{Object.keys(containerStatuses).map(value => <button key={value} type="button" role="option" aria-selected={status === value} className={`wh-menu-item${status === value ? " selected" : ""}`} onClick={() => { setStatus(value); setPage(1); setStatusOpen(false); }}><span>{containerStatuses[value]}</span>{status === value && <Check size={14} />}</button>)}</div>
      </FilterMenu><div className="customer-date-filter">
        <CalendarDateField label="Receiving date from" placeholder="Select FromDate" value={dateFrom} onChange={from => { setDateFrom(from); setDateTo(''); }} />
        <CalendarDateField label="Receiving date to" placeholder="Select ToDate" value={dateTo} min={dateFrom || undefined} onChange={to => {
          setDateTo(to);
          if (dateFrom && to >= dateFrom) { setAppliedDates({ from: dateFrom, to }); setPage(1); }
        }} />
      </div></div>
    </div>
      <CustomerInventoryTable hideWeightUnits statuses={details.statuses} showMetadata showCartonMeasurements summaryInventory={filtered} inventory={filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)} summary={statusSummary} />
      <div className="wh-pager"><p>Showing {filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} Inventory Records</p><div>
        <button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={14} />Previous</button>
        {Array.from({ length: pageCount }, (_, index) => <button key={index} className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => setPage(index + 1)}>{index + 1}</button>)}
        <button disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Next<ChevronRight size={14} /></button>
      </div></div>
    </div>
    {editing && <CustomerModal mode="edit" customer={details.customer} onClose={() => setEditing(false)} onSubmit={saveCustomer} />}
    <Toast toast={toast} onClose={() => setToast(null)} />
  </section>;
}
